import { EmptyState } from '../../components/ui/EmptyState';
import { formatCalendarDate } from '../../lib/format';
import type { TicketListItem } from '../../api/types';
import { TicketMiniList } from './TicketMiniList';
import { describeCount, type ReleaseHistory, type VersionRollup } from './version-rollup';

/**
 * What shipped, in each released version, newest first.
 *
 * ⭐ "SHIPPED IN" IS THE FIX-VERSION RELATIONSHIP, not the version field. A ticket planned for v1.0.33
 * that slipped did not ship in it, and a release note built from `ticket.version_id` would claim it
 * did — which is the single most expensive way these two fields can be confused.
 */
export function ReleaseHistoryView({
  history,
  windowIsComplete,
  onOpenTicket,
}: {
  history: ReleaseHistory;
  windowIsComplete: boolean;
  onOpenTicket: (ticket: TicketListItem) => void;
}) {
  const nothingReleased = history.dated.length === 0 && history.undated.length === 0;

  if (nothingReleased) {
    return (
      <EmptyState
        title="Nothing has been released yet"
        detail="No version in this project is in state `released`. This is an empty history, not a failed one."
      />
    );
  }

  return (
    <div className="space-y-4">
      {history.dated.map((entry) => (
        <ReleaseEntry
          key={entry.version.id}
          rollup={entry}
          when={formatCalendarDate(entry.version.release_date)}
          windowIsComplete={windowIsComplete}
          onOpenTicket={onOpenTicket}
        />
      ))}

      {history.undated.length > 0 ? (
        <section>
          <h3 className="text-xs font-bold tracking-wide text-[color:var(--color-ink-muted)] uppercase">
            Released, with no release date recorded
          </h3>
          {/* ⚠️ Their own group. Sorting them by a missing date would park them at the bottom of the
              list and imply they were the oldest releases, which nothing in the data says. */}
          <div className="mt-2 space-y-4">
            {history.undated.map((entry) => (
              <ReleaseEntry
                key={entry.version.id}
                rollup={entry}
                when="date unknown"
                windowIsComplete={windowIsComplete}
                onOpenTicket={onOpenTicket}
              />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function ReleaseEntry({
  rollup,
  when,
  windowIsComplete,
  onOpenTicket,
}: {
  rollup: VersionRollup;
  when: string;
  windowIsComplete: boolean;
  onOpenTicket: (ticket: TicketListItem) => void;
}) {
  const shipped = rollup.fixedIn;
  const slipped = rollup.plannedIn.filter(
    (ticket) => !shipped.some((entry) => entry.id === ticket.id),
  );
  const shippedCount = describeCount(rollup.fixedTotal, shipped.length, windowIsComplete);

  return (
    <article className="rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-surface-raised)] p-3">
      <header className="flex flex-wrap items-baseline gap-2">
        <h3 className="text-sm font-semibold">{rollup.version.name}</h3>
        <span className="text-xs text-[color:var(--color-ink-muted)]">{when}</span>
        <span className="text-xs text-[color:var(--color-ink-muted)]">
          · {shippedCount.text} shipped
        </span>
      </header>

      {rollup.version.description ? (
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">{rollup.version.description}</p>
      ) : null}

      {shippedCount.hiddenFromList > 0 ? (
        <p className="mt-2 text-[11px] text-[color:var(--color-ink-muted)]">
          Listing {shipped.length} of {shippedCount.text} — the other {shippedCount.hiddenFromList}{' '}
          shipped in this release but are outside the tickets this page loaded.
        </p>
      ) : null}

      {shipped.length === 0 ? (
        <p className="mt-2 text-xs text-[color:var(--color-ink-muted)]">
          {shippedCount.isExact && shippedCount.hiddenFromList > 0
            ? 'None of them are in the tickets this page loaded.'
            : 'No ticket lists this as a fix version. A released version with nothing in it usually means the fix versions were never assigned — not that nothing shipped.'}
        </p>
      ) : (
        /* ⭐ Status is omitted here: in a RELEASE HISTORY every row shipped, and a status chip
           reading `Code Review` beside it is about today rather than about the release. */
        <TicketMiniList tickets={shipped} showStatus={false} onOpenTicket={onOpenTicket} />
      )}

      {slipped.length > 0 ? (
        <p className="mt-2 text-xs text-[color:var(--color-ink-muted)]">
          ⚠️ {slipped.length} ticket
          {slipped.length === 1 ? ' was' : 's were'} planned for {rollup.version.name} but did not
          ship in it — listed under the version above, not here.
        </p>
      ) : null}
    </article>
  );
}
